/**
 * Size guide storefront + theme editor.
 *
 * Storefront: the trigger starts hidden. Entry charts live in hidden
 * `.size-guide-entry-data` nodes. After finding the highest-priority match, the
 * script wires that chart into the trigger dialog and reveals one button.
 * No match (or no entries) → button stays hidden.
 *
 * Theme editor: every entry trigger stays visible; ✅ marks the winner.
 */

/**
 * @param {ParentNode} [root]
 */
function initSizeGuides(root = document) {
  root.querySelectorAll('[data-size-guide]').forEach((blockNode) => {
    if (!(blockNode instanceof HTMLElement)) return;

    if (blockNode.hasAttribute('data-size-guide-editor')) {
      initSizeGuideEditor(blockNode);
      return;
    }

    initSizeGuideStorefront(blockNode);
  });
}

/**
 * @param {HTMLElement} blockEl
 */
function initSizeGuideEditor(blockEl) {
  /** @type {HTMLElement[]} */
  const entries = [
    ...blockEl.querySelectorAll('.size-guide-entry[data-size-guide-priority]'),
  ].filter((node) => node instanceof HTMLElement);

  if (entries.length === 0) return;

  let bestPriority = 0;
  /** @type {HTMLElement | null} */
  let winner = null;

  for (const entry of entries) {
    const priority = Number(entry.dataset.sizeGuidePriority || 0);
    entry.classList.remove('is-match');
    const badge = entry.querySelector('.size-guide-entry__match-badge');
    if (badge instanceof HTMLElement) badge.hidden = true;

    if (priority > bestPriority) {
      bestPriority = priority;
      winner = entry;
    }
  }

  if (!winner || bestPriority <= 0) return;

  winner.classList.add('is-match');
  const badge = winner.querySelector('.size-guide-entry__match-badge');
  if (badge instanceof HTMLElement) badge.hidden = false;
}

/**
 * @param {HTMLElement} blockEl
 */
function initSizeGuideStorefront(blockEl) {
  const staticShell = blockEl.querySelector('.size-guide');
  if (!(staticShell instanceof HTMLElement)) return;

  // Always start with no visible button until a match is confirmed.
  staticShell.hidden = true;

  /** @type {HTMLElement[]} */
  const entryNodes = [
    ...blockEl.querySelectorAll(
      '.size-guide-entry-data[data-size-guide-priority]'
    ),
  ].filter((node) => node instanceof HTMLElement);

  let bestPriority = 0;
  /** @type {HTMLElement | null} */
  let winner = null;

  for (const entryNode of entryNodes) {
    const priority = Number(entryNode.dataset.sizeGuidePriority || 0);
    if (priority > bestPriority) {
      bestPriority = priority;
      winner = entryNode;
    }
  }

  if (!winner || bestPriority <= 0) {
    return;
  }

  const dialogContent = staticShell.querySelector('dialog-content');
  if (!(dialogContent instanceof HTMLElement)) {
    return;
  }

  let targetTemplate = dialogContent.querySelector('template');
  if (!(targetTemplate instanceof HTMLTemplateElement)) {
    targetTemplate = document.createElement('template');
    dialogContent.replaceChildren(targetTemplate);
  }

  targetTemplate.innerHTML = winner.innerHTML;
  staticShell.hidden = false;
  staticShell.classList.add('is-ready');
}

initSizeGuides();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element || target instanceof Document) {
    initSizeGuides(target);
  }
});

document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  const section =
    target instanceof Element ? target.closest('.shopify-section') : null;
  initSizeGuides(section || document);
});
